import React, { useState, useEffect, useRef } from 'react';
import { Form, Spinner, Button } from 'react-bootstrap';
import { getInvoices } from '../../api/generated/arrayscapeGamingDashboardAPI';
import type { Invoice } from '../../api/generated/model';

const formatCurrency = (s?: string): string => {
  if (!s) return '';
  const n = parseFloat(s);
  if (Number.isNaN(n)) return s;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
};

const formatDate = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
};

// Invoice.invoice_number from the API is already the display form
// (DisplayNumber() server-side concatenates the suffix).
const displayNumber = (inv: Invoice): string => inv.invoice_number || '';
// The API's Invoice.id is already in `<src_id>-<id>` composite form
// (Invoice.CompositeID() on the backend), so pass it through verbatim
// — building it a second time would double-prepend src_id.
const compositeId = (inv: Invoice): string | null => inv.id || null;

interface InvoicePickerProps {
  value: Invoice | null;
  onChange: (inv: Invoice | null) => void;
  /**
   * Optional callback to open an invoice detail modal. When set, each
   * result row + the selected chip get a "Open" icon button. The parent
   * is responsible for actually mounting the InvoiceDetailModal.
   */
  onOpenInvoiceDetail?: (compositeId: string) => void;
  /** Pre-fill the search input on first render (e.g. parsed from a memo). */
  initialSearch?: string;
  placeholder?: string;
  size?: 'sm';
  disabled?: boolean;
  /** Min length before triggering a search. Default 2. */
  minSearchLength?: number;
}

/**
 * Searchable invoice picker. Type to debounce-query the invoices API by
 * invoice number; pick a result to set the value. Shared by the Create
 * Payment flow and the Manual Remittance editor so both go through the
 * same shape (and can't accept invoice numbers that don't actually exist).
 */
const InvoicePicker: React.FC<InvoicePickerProps> = ({
  value,
  onChange,
  onOpenInvoiceDetail,
  initialSearch,
  placeholder = 'Search invoice #...',
  size,
  disabled,
  minSearchLength = 2,
}) => {
  const [search, setSearch] = useState(initialSearch || '');
  const [results, setResults] = useState<Invoice[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  // Direction the dropdown opens. Recomputed each time `open` flips so
  // the picker doesn't push the modal into a scroll when there's no
  // room below the input.
  const [openUpward, setOpenUpward] = useState(false);
  // Tracks whether the user has actively interacted with this picker
  // (focused or typed). A seeded initial search runs in the background
  // but we don't pop the dropdown open until the user looks at it.
  const userInteractedRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search.
  useEffect(() => {
    if (!search || search.length < minSearchLength) {
      setResults([]);
      return;
    }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const result = await getInvoices({
          q: search,
          limit: 10,
          sort_field: 'invoice_date' as any,
          sort_direction: 'desc' as any,
        });
        setResults((result as any)?.data || []);
        // Don't auto-pop the dropdown for a backgrounded seeded search.
        // The user will see results when they actually focus the input.
        if (userInteractedRef.current) setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [search, minSearchLength]);

  // Close on outside click. Means clicking another picker's input — or
  // anywhere else on the page — dismisses this dropdown immediately,
  // independent of the input blur timing.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // When opening, decide whether to drop down or pop up based on the
  // space below the input vs above. Picks "above" only when there isn't
  // room below AND there's more room above. Avoids forcing the
  // surrounding modal/page to scroll just to reveal the dropdown.
  const dropdownMaxHeight = 280;
  useEffect(() => {
    if (!open) return;
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setOpenUpward(spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow);
  }, [open]);

  // Selected chip view. Plain inline-flex container instead of <Badge>,
  // which Bootstrap renders in small text — we want the chip readable at
  // the surrounding font size.
  if (value) {
    const id = compositeId(value);
    return (
      <div className="d-flex align-items-center gap-2 flex-wrap border rounded px-2 py-1 bg-body-tertiary">
        <span className="fw-semibold">#{displayNumber(value)}</span>
        {value.customer?.name && <span className="text-muted">· {value.customer.name}</span>}
        {value.invoice_date && <span className="text-muted">· {formatDate(value.invoice_date)}</span>}
        {(value.net_amount || value.invoice_amount) && (
          <span className="text-muted">· {formatCurrency(value.net_amount || value.invoice_amount)}</span>
        )}
        {onOpenInvoiceDetail && id && (
          <Button
            variant="link"
            size="sm"
            className="p-0 ms-1"
            onClick={() => onOpenInvoiceDetail(id)}
            title="Open invoice details"
          >
            <i className="bi bi-box-arrow-up-right"></i>
          </Button>
        )}
        {!disabled && (
          <Button
            variant="link"
            size="sm"
            className="p-0 text-muted"
            onClick={() => onChange(null)}
            title="Clear"
          >
            <i className="bi bi-x-lg"></i>
          </Button>
        )}
      </div>
    );
  }

  // Search view.
  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <Form.Control
        type="text"
        placeholder={placeholder}
        value={search}
        size={size}
        disabled={disabled}
        onChange={(e) => {
          userInteractedRef.current = true;
          setSearch(e.target.value);
        }}
        onFocus={() => {
          userInteractedRef.current = true;
          if (results.length > 0) setOpen(true);
        }}
      />
      {searching && (
        <Spinner
          size="sm"
          className="position-absolute"
          style={{ right: '10px', top: '8px' }}
        />
      )}
      {open && results.length > 0 && (
        <div
          className="position-absolute bg-body border rounded shadow-sm"
          style={{
            zIndex: 1060,
            maxHeight: `${dropdownMaxHeight}px`,
            overflow: 'auto',
            minWidth: '360px',
            ...(openUpward
              ? { bottom: '100%', marginBottom: '2px' }
              : { top: '100%', marginTop: '2px' }
            ),
          }}
        >
          {results.map((inv) => {
            const id = compositeId(inv);
            return (
              <div
                key={`${inv.src_id}:${inv.id}`}
                className="px-3 py-2 border-bottom d-flex justify-content-between align-items-center gap-2"
                style={{ cursor: 'pointer' }}
                onMouseDown={(e) => {
                  // mouseDown so onBlur of the input doesn't kill the
                  // dropdown before the click registers.
                  e.preventDefault();
                  onChange(inv);
                  setSearch('');
                  setResults([]);
                  setOpen(false);
                }}
              >
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="d-flex align-items-baseline gap-2 flex-wrap">
                    <strong>#{displayNumber(inv)}</strong>
                    {inv.customer?.name && (
                      <span className="text-muted text-truncate">{inv.customer.name}</span>
                    )}
                  </div>
                  <div className="small text-muted d-flex gap-2">
                    {inv.invoice_date && <span>{formatDate(inv.invoice_date)}</span>}
                    {(inv.net_amount || inv.invoice_amount) && (
                      <span>{formatCurrency(inv.net_amount || inv.invoice_amount)}</span>
                    )}
                  </div>
                </div>
                {onOpenInvoiceDetail && id && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-muted flex-shrink-0"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onOpenInvoiceDetail(id);
                    }}
                    title="Open invoice details"
                  >
                    <i className="bi bi-box-arrow-up-right"></i>
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InvoicePicker;
